import { type FC, type MouseEvent } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedNumber, FormattedMessage } from 'dibs-react-intl';
import slugify from 'dibs-slugify/exports/slugify.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './SbSharedRefineMenuSearchResults.module.css';

import { type SbSharedRefineMenuSearchResults_itemFacetSearch$key } from './__generated__/SbSharedRefineMenuSearchResults_itemFacetSearch.graphql.ts';
import { type FilterValue } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelect.tsx';

const itemFacetSearchFragment = graphql`
    fragment SbSharedRefineMenuSearchResults_itemFacetSearch on ItemFacetsQueryConnection {
        edges {
            node {
                urlLabel
                linkReference
                displayName
                count
            }
        }
    }
`;

type Props = {
    itemFacetSearch: SbSharedRefineMenuSearchResults_itemFacetSearch$key | null | undefined;
    ariaLive?: 'off' | 'polite';
    excludedDisplayNames: string[];
    iconMap?: Record<string, FC<{ className?: string }>>;
    id: string;
    isLoading: boolean;
    onResultClick: (result: FilterValue, event: MouseEvent<HTMLElement>) => void;
    searchString: string;
};

export const SbSharedRefineMenuSearchResults: FC<Props> = ({
    itemFacetSearch: itemFacetSearchRef,
    ariaLive,
    excludedDisplayNames = [],
    iconMap,
    id,
    isLoading,
    onResultClick,
    searchString,
}) => {
    const { isDesktop } = useServerVarsContext();
    const itemFacetSearch = useFragment(itemFacetSearchFragment, itemFacetSearchRef);
    const normalizedResults = (itemFacetSearch?.edges || [])
        .map(edge => edge?.node)
        .filter(filterFalsy);
    const resultsToShow = normalizedResults
        .slice(0, 5)
        .filter(
            result => result?.displayName && !excludedDisplayNames.includes(result.displayName)
        );

    let markup;

    if (resultsToShow.length) {
        markup = (
            <ul className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapYMedium)}>
                {resultsToShow.map(({ urlLabel, displayName, count, linkReference }) => {
                    const IconComponent = urlLabel ? iconMap?.[urlLabel] : undefined;
                    return (
                        <li key={urlLabel}>
                            <Link
                                buyerLinkType="linkNoDecoration"
                                className={classnames(
                                    styles.result,
                                    dibsCss.wFull,
                                    dibsCss.flex,
                                    dibsCss.justifyBetween,
                                    {
                                        [dibsCss.cursorPointer]: isDesktop,
                                        [dibsCss.sassyFontBodySizeSmallTypeNone]: isDesktop,
                                    }
                                )}
                                onClick={(event: MouseEvent<HTMLElement>) =>
                                    onResultClick(
                                        { urlLabel, displayName, count, linkReference },
                                        event
                                    )
                                }
                                dataTn={`search-result-${slugify(displayName)}`}
                            >
                                <span>
                                    {IconComponent && (
                                        <IconComponent
                                            className={classnames(
                                                styles.searchResultIcon,
                                                dibsCss.mrSmall,
                                                dibsCss.alignMiddle,
                                                dibsCss.textStorm
                                            )}
                                        />
                                    )}
                                    {displayName}
                                </span>
                                {typeof count === 'number' && <FormattedNumber value={count} />}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        );
        /**
         * If the itemFacetSearch?.edges is defined, that means we received a response from the server.
         * Is loading determines wheter the request is in flight or not.
         * The normalizedResults check indicates wheter we do not have any results to show because
         * the user has selected all of them.
         *
         */
    } else if (itemFacetSearch?.edges && !isLoading && normalizedResults.length === 0) {
        markup = (
            <FormattedMessage
                id="sb.SbMobileRefineMenu.itemFacetSearch.noResults"
                defaultMessage="No results for {searchString}"
                values={{ searchString }}
            />
        );
    } else {
        return null;
    }

    return (
        <div
            id={id}
            aria-live={ariaLive}
            className={classnames(dibsCss.myMedium, dibsCss.mx0)}
            data-tn={`no-results-${slugify(searchString)}`}
        >
            {markup}
        </div>
    );
};
